import { useState, type FormEvent } from "react";
import { brandName, ctaPoints } from "@/mocks/home";

const FORM_ID = "dainf6bp14a1h3mr77jg";
const SUBMIT_ADDR = "https://readdy.ai/api/form/dainf6bp14a1h3mr77jg";

export default function Subscribe() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      return;
    }
    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (!isOk) {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交没有成功，请稍后重试。",
        );
        return;
      }

      setStatus("success");
      setFormError("");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络出现异常，请检查后重试。");
    } finally {
      setStatus((prev) => (prev === "submitting" ? "idle" : prev));
    }
  };

  return (
    <section id="subscribe" className="bg-background-100 scroll-mt-20">
      <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="border-b border-background-200 p-6 md:p-9 lg:border-b-0 lg:border-r">
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1.5">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-send-plane-line text-sm text-primary-700" />
                </span>
                <span className="font-label text-xs tracking-[0.12em] text-primary-700">
                  热点速递
                </span>
              </span>
              <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
                不想错过任何一个热点
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                留下邮箱，我们把当天讨论最集中的事件与网友观点整理成一份速递发给你。看大家怎么说，一次看够。
              </p>

              <ul className="mt-6 space-y-3">
                {ctaPoints.map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-accent-600">
                      <i className="ri-checkbox-circle-line text-lg" />
                    </span>
                    <span className="text-sm text-foreground-700">{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-6 md:p-9">
              <form
                id={FORM_ID}
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
              >
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="sub-email" className="font-label text-sm text-foreground-700">
                    邮箱地址
                  </label>
                  <input
                    id="sub-email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="sub-interest" className="font-label text-sm text-foreground-700">
                    最想关注的板块
                  </label>
                  <select
                    id="sub-interest"
                    name="interest"
                    defaultValue="影视综"
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
                  >
                    <option value="影视综">影视综</option>
                    <option value="消费观察">消费观察</option>
                    <option value="直播电商">直播电商</option>
                    <option value="社区热议">社区热议</option>
                    <option value="品牌公关">品牌公关</option>
                    <option value="数码体验">数码体验</option>
                  </select>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="hp-field-marker"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="mt-1 h-11 whitespace-nowrap rounded-md bg-primary-500 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {status === "submitting" ? "正在提交…" : "免费订阅热点速递"}
                </button>

                <p className="text-xs text-foreground-500">
                  订阅即表示你同意我们仅将邮箱用于发送 {brandName} 的热点速递。
                </p>

                {status === "success" ? (
                  <p className="flex items-center gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-900">
                    <i className="ri-checkbox-circle-fill" />
                    订阅成功，感谢加入 {brandName} 的讨论圈。
                  </p>
                ) : null}
                {status === "error" ? (
                  <p className="flex items-center gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-sm text-primary-700">
                    <i className="ri-error-warning-line" />
                    {formError}
                  </p>
                ) : null}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}